/**
 * Utility functions for handling template files dynamically
 * This module provides functionality to automatically detect and use the latest template version
 */

export interface TemplateInfo {
  filename: string;
  version: number;
  downloadName: string;
}

/**
 * Extracts version number from template filename
 * Supports formats like: RC_event_import_template_v15.xlsm
 */
export function extractVersionNumber(filename: string): number {
  const match = filename.match(/v(\d+)\.xlsm$/);
  return match ? parseInt(match[1]) : 0;
}

/**
 * Gets the latest RC event import template information
 * This function dynamically determines the latest version available
 */
export function getLatestTemplateInfo(): TemplateInfo {
  // List of ACTUAL template files that exist - only add versions that are uploaded
  const knownTemplates = [
    'RC_event_import_template_v15.xlsm',
    'RC_event_import_template_v16.xlsm',
    'RC_event_import_template_v17.xlsm',
    // Add future versions here ONLY when they actually exist
  ];
  
  // Find the latest version by sorting version numbers
  const latestTemplate = knownTemplates
    .sort((a, b) => extractVersionNumber(b) - extractVersionNumber(a))[0];
  
  if (!latestTemplate) {
    // Fallback to v17 if no templates found
    return {
      filename: 'RC_event_import_template_v17.xlsm',
      version: 16,
      downloadName: 'RC_event_import_template_v17'
    };
  }
  
  const version = extractVersionNumber(latestTemplate);
  const downloadName = latestTemplate.replace('.xlsm', '');
  
  return {
    filename: latestTemplate,
    version,
    downloadName
  };
}

/**
 * Gets the template URL for download
 */
export function getTemplateUrl(): string {
  const templateInfo = getLatestTemplateInfo();
  return `/${templateInfo.filename}`;
}

/**
 * Gets the download filename (without extension)
 */
export function getTemplateDownloadName(): string {
  const templateInfo = getLatestTemplateInfo();
  return templateInfo.downloadName;
}

